import _ from "lodash";
import { DateColumnTypeEnum, FileContainsTypeEnum, IColumn, IConfiguration, IFileStoreInfo, IObjectMap, IObjectMapInfo, ITagType, ITagTypeMap, ITagTypeMapInfo, ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { IReferenceTimeZoneDTO } from "../../../services/global-data-service/Models";
import { FormRadioGroupRadioButton } from "../../../components/forms/form-radio-group/FormRadioGroup";
import i18n from "../../../services/localization-service/i18n";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import { SetPointEventTypeEnum } from "../../../common/enums/SetPointEventTypeEnum";

export enum DataFileImportAdminMode {
    Add = 0,
    Edit = 1
}

export enum StepEnum {
    FileInformation = 1,
    Objects = 2,
    Data = 3
}

export const DISPLAYNAME = 'Display Name';

// export type ReportScheduleRecipientType = {
//     id: number;
//     address: string;
//     organizationID: number;
//     selected: boolean;
// } & VirtualizedListItem

export type ItemMapFormType = {
    fieldKey: string;
    column: string;
    id: number;
    objectType: eLynxObjectTypeEnum;
    name: string;
    guid?: string;
    eventType?: SetPointEventTypeEnum;
}

export type DataFileImportFormType = {

    fileName: string;
    fileStoreInfo: IFileStoreInfo | undefined;
    timezoneIndex: number;
    tagTypeMap: ItemMapFormType[];
    objectMap: ItemMapFormType[];
    attributeName: string;


} & Omit<IConfiguration, 'timezone' | 'tagTypeMap' | 'objectMap'>;

export const fileContainsItems: FormRadioGroupRadioButton[] = [{
    label: i18n.t('dataFileImport.fileContainsTypes.dataForSingleDevice'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForSingleDevice') as string,
    value: FileContainsTypeEnum.DataForSingleDevice
}, {
    label: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleDevices'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleDevices') as string,
    value: FileContainsTypeEnum.DataForMultipleDevices
}, {
    label: i18n.t('dataFileImport.fileContainsTypes.dataForSingleWell'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForSingleWell') as string,
    value: FileContainsTypeEnum.DataForSingleWell
}, {
    label: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleWells'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleWells') as string,
    value: FileContainsTypeEnum.DataForMultipleWells
}, {
    label: i18n.t('dataFileImport.fileContainsTypes.dataForSingleTank'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForSingleTank') as string,
    value: FileContainsTypeEnum.DataForSingleTank
}, {
    label: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleTanks'),
    ariaLabel: i18n.t('dataFileImport.fileContainsTypes.dataForMultipleTanks') as string,
    value: FileContainsTypeEnum.DataForMultipleTanks
}];

export const dateColumnTypeItems: FormRadioGroupRadioButton[] = [{
    label: i18n.t('dataFileImport.dateColumnTypes.one'),
    ariaLabel: i18n.t('dataFileImport.dateColumnTypes.one') as string,
    value: DateColumnTypeEnum.One
},{
    label: i18n.t('dataFileImport.dateColumnTypes.two'),
    ariaLabel: i18n.t('dataFileImport.dateColumnTypes.two') as string,
    value: DateColumnTypeEnum.Two
}];

const defaultColumn: IColumn = {
    id: -1,
    name: ''
};

export const defaultImport: DataFileImportFormType = {
    id: -1,
    name: '',

    fileName: '',
    fileStoreInfo: undefined,

    attributeName: '',

    columns: [],
    dateColumn: defaultColumn,
    dateColumnType: DateColumnTypeEnum.One,
    fileContainsType: FileContainsTypeEnum.DataForSingleDevice,
    fileHasHeaderRow: false,
    identifierColumn: defaultColumn,
    importObjectType: eLynxObjectTypeEnum.None,
    importType: ImportTypeEnum.None,

    objectMap: [],
    tagTypeColumns: [],
    tagTypeMap: [],
    timeColumn: defaultColumn,
    timezoneIndex: 20
}

const DataFileImportEditorUtils = {

    mapToModel: (form: DataFileImportFormType, 
        tagTypes: ITagTypeMapInfo,
        importObjects: IObjectMapInfo,
        timezones: IReferenceTimeZoneDTO[]
    ): IConfiguration => {

        const model: IConfiguration = {
            id: form.id,
            name: form.name,
            columns: _.cloneDeep(form.columns),
            dateColumn: form.columns.find(o => o.name === form.dateColumn.name) as IColumn,
            dateColumnType: form.dateColumnType,
            fileContainsType: form.fileContainsType,
            fileHasHeaderRow: form.fileHasHeaderRow,
            identifierColumn: form.columns.find(o => o.name === form.identifierColumn.name) ?? { id: -1, name: ''} as IColumn,
            importObjectType: form.importObjectType,
            importType: form.importType,
            objectMap: form.importType === ImportTypeEnum.Multiple ? form.objectMap.map(o => {

                const entity = importObjects.objects.find(v => v.id === o.id && v.objectType === o.objectType);

                return {
                    identifier: o.column,
                    mappedObject: {
                        id: o.id,
                        objectType: o.objectType,
                        objectName: entity?.objectName,
                        displayText: entity?.displayText,
                        guid: entity?.guid
                    } as IeLynxObject
                } as IObjectMap;
            }) : undefined,
            tagTypeColumns: form.tagTypeMap.map(o => {
                return {
                    id: form.columns.find(v => v.name === o.column)?.id,
                    name: o.column

                } as IColumn;

            }),
            tagTypeMap: form.tagTypeMap.map(o => {
                return {
                    importColumn: form.columns.find(v => v.name === o.column) as IColumn,
                    mappedTagType: {
                        id: o.id,
                        name: o.id === -2 ? "Ignore this TAT" : tagTypes.tagTypes.find(v => v.id === o.id)?.name
                    } as ITagType,
                    eventType: o.eventType ?? SetPointEventTypeEnum.Imported
                } as ITagTypeMap;
            }),
            timeColumn: form.columns.find(o => o.name === form.timeColumn?.name),
            timezone: timezones.find(o => o.id === form.timezoneIndex) as IReferenceTimeZoneDTO,
            attributeName: form.attributeName
        };

        return model;
    },
    mapToFormValues: (model: IConfiguration,
        timezones: IReferenceTimeZoneDTO[]
    ): DataFileImportFormType  => {

        const formValues: DataFileImportFormType = {..._.cloneDeep(model),
            timezoneIndex: timezones.find(o => o.id === model.timezone?.id)?.id ?? 20,
            fileName: '',
            fileStoreInfo: undefined,
            attributeName: model.attributeName ?? '',
            tagTypeMap: model.id <= 0 ? 
                            [] :
                        model.tagTypeMap.map(o => {
                            return {
                                column: o.importColumn.name,
                                id: o.mappedTagType.id,
                                name: o.mappedTagType.name,
                                objectType: eLynxObjectTypeEnum.Tag,
                                eventType: o.eventType

                            } as ItemMapFormType;
                        }),
            objectMap: model.id <= 0 ?
                         [] :
                         model.objectMap?.map(o => {
                            return {
                                fieldKey: `om-${o.identifier}`,
                                column: o.identifier,
                                id: o.mappedObject.id,
                                name: o.mappedObject.displayText,
                                objectType: o.mappedObject.objectType,
                                guid: o.mappedObject.guid

                            } as ItemMapFormType;
                         }) ?? []
        };

        return formValues;
    },
}

export default DataFileImportEditorUtils;
